import Label from "components/Label/Label";
import React, { FC, useState, useEffect, useRef } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import Textarea from "shared/Textarea/Textarea";
import CommonLayout from "./CommonLayout";
import { Helmet } from "react-helmet-async";
import { avatarImgs } from "contains/fakeData";
import { updateUserProfile } from "api";
import { SweetToast } from "utils/sweetAlert";

export interface AccountPageProps {
  className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = "" }) => {
  // State for user data
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [userGender, setUserGender] = useState("");
  const [userDob, setUserDob] = useState("");
  const [userAbout, setUserAbout] = useState("");
  const [userAvatar, setUserAvatar] = useState(avatarImgs[2]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const defaultAvatar = avatarImgs[2];

  // Load user data from localStorage
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userDataStr = localStorage.getItem("userData");
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          
          // Set values with various possible field names
          setUserName(userData?.name || userData?.fullName || userData?.full_name || "");
          setUserEmail(userData?.email || "");
          setUserMobile(userData?.mobile || userData?.phone || userData?.phoneNumber || "");
          setUserAddress(
            userData?.address || 
            userData?.fullAddress || 
            userData?.full_address || 
            userData?.userAddress?.address ||
            userData?.userAddress ||
            ""
          );
          setUserGender(userData?.gender || "");
          setUserDob(userData?.dob || userData?.dateOfBirth || userData?.date_of_birth || userData?.birthDate || "");
          setUserAbout(userData?.about || userData?.bio || "");
          
          // Set avatar with default fallback if not available - check for profilePicture field first (user_profile table)
          const avatarUrl = userData?.profilePicture || userData?.profileImage || userData?.user_photo || userData?.avatar || userData?.image;
          // Always use default avatar if no profile photo exists
          setUserAvatar(avatarUrl ? avatarUrl : defaultAvatar);
        }
      } catch (error) {
        console.error("Error parsing userData:", error);
        // Set default avatar on error
        setUserAvatar(defaultAvatar);
      }
    };

    loadUserData();
  }, []);

  // Handle profile image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type - allowed formats: JPEG, JPG, PNG, GIF, WebP
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      const allowedExtensions = ["jpeg", "jpg", "png", "gif", "webp"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type.toLowerCase()) && !allowedExtensions.includes(fileExtension || "")) {
        SweetToast.error("Please select a valid image file (JPEG, JPG, PNG, GIF, or WebP)");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        SweetToast.error("Image size should be less than 5MB");
        return;
      }

      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle update account
  const handleUpdateAccount = async () => {
    try {
      // Validate required fields
      if (!userName.trim()) {
        SweetToast.error("Full name is required");
        return;
      }

      if (!userEmail.trim()) {
        SweetToast.error("Email is required");
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userEmail)) {
        SweetToast.error("Please enter a valid email address");
        return;
      }

      setIsUpdating(true);

      // Prepare profile data - ensure address is included
      const profileData: any = {
        name: userName,
        email: userEmail,
        mobile: userMobile || null,
        phone: userMobile || null,
        address: userAddress || null,
        gender: userGender || null,
        dob: userDob || null,
        dateOfBirth: userDob || null,
        about: userAbout || null,
      };

      // Call update API with profile data and image file
      // The API will send the image as 'profilePicture' field for backend (user_profile table)
      // Note: When gender or dob is changed, mobile and profilePicture should also be updated in user_profile table
      const response = await updateUserProfile(profileData, selectedImage);

      if (response?.success) {
        // Update localStorage with new user data - check for profilePicture field from backend (user_profile table)
        const updatedUserData = {
          ...JSON.parse(localStorage.getItem("userData") || "{}"),
          ...profileData,
          address: userAddress, // Ensure address is updated
          mobile: userMobile, // Ensure mobile is updated
          profilePicture: response?.data?.profilePicture || response?.data?.profileImage || response?.data?.user_photo || response?.data?.avatar || response?.data?.image || userAvatar,
          profileImage: response?.data?.profilePicture || response?.data?.profileImage || response?.data?.user_photo || response?.data?.avatar || response?.data?.image || userAvatar,
          user_photo: response?.data?.profilePicture || response?.data?.profileImage || response?.data?.user_photo || response?.data?.avatar || response?.data?.image || userAvatar,
          avatar: response?.data?.profilePicture || response?.data?.profileImage || response?.data?.user_photo || response?.data?.avatar || response?.data?.image || userAvatar,
          image: response?.data?.profilePicture || response?.data?.profileImage || response?.data?.image || response?.data?.avatar || userAvatar,
        };

        localStorage.setItem("userData", JSON.stringify(updatedUserData));

        // Update avatar if new image was uploaded - prioritize profilePicture field (user_profile table)
        const newAvatarUrl = response?.data?.profilePicture || response?.data?.profileImage || response?.data?.user_photo || response?.data?.avatar || response?.data?.image;
        if (newAvatarUrl) {
          setUserAvatar(newAvatarUrl);
        } else if (avatarPreview) {
          // If no response URL but we have a preview, use the preview
          setUserAvatar(avatarPreview);
        }

        // Clear selected image and preview
        setSelectedImage(null);
        setAvatarPreview("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        SweetToast.success("Account updated successfully!");
        
        // Refresh page after a short delay to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        SweetToast.error(response?.message || "Failed to update account. Please try again.");
      }
    } catch (error: any) {
      console.error("Error updating profile:", error);
      SweetToast.error(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to update account. Please try again."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>Account || Ciseco ecommerce React Template</title>
      </Helmet>
      <CommonLayout>
        <div className="space-y-10 sm:space-y-12">
          {/* HEADING */}
          <h2 className="text-2xl sm:text-3xl font-semibold">
            Account infomation
          </h2>
          <div className="flex flex-col md:flex-row">
            <div className="flex-shrink-0 flex items-start">
              {/* AVATAR */}
              <div className="relative rounded-full overflow-hidden flex">
                <img
                  src={avatarPreview || userAvatar || defaultAvatar}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover z-0"
                  onError={(e) => {
                    // Fallback to default avatar if image fails to load
                    (e.target as HTMLImageElement).src = defaultAvatar;
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="mt-1 text-xs">Change Image</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleImageChange}
                />
              </div>
            </div>
            <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6">
              <div>
                <Label>Full name</Label>
                <Input className="mt-1.5" value={userName} onChange={(e) => setUserName(e.target.value)} />
              </div>

              {/* ---- */}

              {/* ---- */}
              <div>
                <Label>Email</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-envelope"></i>
                  </span>
                  <Input
                    className="!rounded-l-none"
                    placeholder="example@email.com"
                    value={userEmail}
                    readOnly
                  />
                </div>
              </div>

              {/* ---- */}
              <div className="max-w-lg">
                <Label>Date of birth</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-calendar"></i>
                  </span>
                  <Input
                    className="!rounded-l-none"
                    type="date"
                    value={userDob}
                    onChange={(e) => setUserDob(e.target.value)}
                  />
                </div>
              </div>
              {/* ---- */}
              <div>
                <Label>Addess</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-map-signs"></i>
                  </span>
                  <Input
                    className="!rounded-l-none"
                    value={userAddress}
                    onChange={(e) => setUserAddress(e.target.value)}
                    placeholder="Enter your address"
                  />
                </div>
              </div>

              {/* ---- */}
              <div>
                <Label>Gender</Label>
                <Select className="mt-1.5" value={userGender} onChange={(e) => setUserGender(e.target.value)}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Select>
              </div>

              {/* ---- */}
              <div>
                <Label>Phone number</Label>
                <div className="mt-1.5 flex">
                  <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                    <i className="text-2xl las la-phone-volume"></i>
                  </span>
                  <Input
                    className="!rounded-l-none"
                    value={userMobile}
                    onChange={(e) => setUserMobile(e.target.value)}
                  />
                </div>
              </div>
              {/* ---- */}
              <div>
                <Label>About you</Label>
                <Textarea className="mt-1.5" value={userAbout} onChange={(e) => setUserAbout(e.target.value)} />
              </div>
              <div className="pt-2">
                <ButtonPrimary 
                  onClick={handleUpdateAccount}
                  disabled={isUpdating}
                >
                  {isUpdating ? "Updating..." : "Update account"}
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountPage;
